/**
 * Meta (Facebook) Pixel + Conversions API bridge — thin, safe wrapper.
 *
 * The base loader + `fbq('init', ...)` live in public/index.html so the library
 * is ready before React mounts. PageViews are fired from the marketing layout
 * only, so the private admin/console surfaces never pollute ad data.
 *
 * Pixel ID: 428051070301352 (public client-side identifier — not a secret).
 *
 * Features:
 *  - UTM tags + fbclid are read from the URL and attached to every event.
 *  - Conversion events (Lead / CompleteRegistration / ViewContent) are ALSO sent
 *    server-side via our backend Conversions API endpoint, sharing the same
 *    `eventID` so Meta de-duplicates them. This keeps conversions tracking even
 *    when a browser blocks the pixel.
 *  - Test Mode (?pixeltest=on): suppresses the browser pixel and sends
 *    test-coded server events so you can verify in Events Manager → Test Events
 *    without firing real browser pixel data.
 *
 * Every call is guarded so analytics can never break the site.
 */
const CAPI_URL = `${process.env.REACT_APP_BACKEND_URL}/api/meta/capi`;
const CAPI_EVENTS = new Set(["Lead", "CompleteRegistration", "ViewContent"]);
const TEST_KEY = "id_pixel_test";

const fb = (...args) => {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq(...args);
    }
  } catch (_) {
    /* never break the site because of the pixel */
  }
};

const makeEventId = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// ?pixeltest=on / ?pixeltest=off flips a persisted flag; returns current state.
export const isTestMode = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("pixeltest");
    if (q === "on") localStorage.setItem(TEST_KEY, "1");
    else if (q === "off") localStorage.removeItem(TEST_KEY);
    return localStorage.getItem(TEST_KEY) === "1";
  } catch (_) {
    return false;
  }
};

// Pull UTM + Meta click id from the current URL (attribution).
export const getUtm = () => {
  const out = {};
  try {
    const q = new URLSearchParams(window.location.search);
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = q.get(k);
      if (v) out[k] = v;
    });
    const fbclid = q.get("fbclid");
    if (fbclid) out.fbclid = fbclid;
  } catch (_) {}
  return out;
};

// Fire-and-forget server-side event (dedup via shared event_id).
const sendCapi = (eventName, eventId, customData, email, test) => {
  try {
    fetch(CAPI_URL, {
      method: "POST",
      credentials: "include", // include _fbp/_fbc first-party cookies
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        email: email || null,
        custom_data: customData || {},
        test: !!test,
      }),
    }).catch(() => {});
  } catch (_) {
    /* never break the site */
  }
};

// Fire a PageView (browser only). Suppressed in Test Mode. StrictMode-deduped.
export const pixelPageView = () => {
  if (isTestMode()) return;
  const p = typeof window !== "undefined" ? window.location.pathname : "";
  const now = Date.now();
  if (pixelPageView._last && pixelPageView._last.p === p && now - pixelPageView._last.t < 1500) return;
  pixelPageView._last = { p, t: now };
  fb("track", "PageView", getUtm());
};

/**
 * Fire a standard Meta conversion event on the browser Pixel and (for
 * conversion events) the server-side Conversions API with a shared event id.
 *
 * @param {string} event  e.g. "Lead", "CompleteRegistration", "ViewContent"
 * @param {object} params custom_data (merged with UTM tags)
 * @param {object} opts   { email } for server-side hashed matching
 * @returns {string} the shared eventID
 */
export const pixelTrack = (event, params, opts) => {
  const eventID = makeEventId();
  const test = isTestMode();
  const customData = { ...getUtm(), ...(params || {}) };

  // Test Mode suppresses the browser pixel so real data isn't polluted.
  if (!test) {
    fb("track", event, customData, { eventID });
  }

  if (CAPI_EVENTS.has(event)) {
    sendCapi(event, eventID, customData, opts && opts.email, test);
  }
  return eventID;
};
